import React, { useCallback, useEffect, useState } from "react";

import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";

import { Shimmer } from "../MotionPrimitives";

const AMAZON_MANAGE_SUBSCRIPTION_URL =
  "https://www.amazon.com/gp/mas/your-account/myapps";

export const isManualSubscriptionCancellation = (subscription) =>
  Boolean(
    subscription?.isResellerPackage ||
      subscription?.canRequestManualCancellation ||
      subscription?.cancellationMode === "support"
  );

export const isExternalSubscriptionManagement = (subscription) =>
  Boolean(
    subscription?.cancellationMode === "external" ||
      subscription?.managementMode === "amazon_appstore"
  );

export const canOpenSubscriptionCancellationModal = (subscription) => {
  const billingSummary = subscription?.billingSummary || {};
  const billingStatus = billingSummary.status || subscription?.billingStatus;
  const usageStatus = subscription?.lunaKey?.usageStatus || "missing";
  const isCancelledSubscription =
    ["canceled", "cancelled"].includes(billingStatus) ||
    usageStatus === "cancelled";

  if (isCancelledSubscription) return false;
  if (isExternalSubscriptionManagement(subscription)) return true;
  if (isManualSubscriptionCancellation(subscription)) {
    return true;
  }

  const cancelAtPeriodEnd =
    billingSummary.cancelAtPeriodEnd ?? subscription?.cancelAtPeriodEnd;

  return !cancelAtPeriodEnd && Boolean(subscription?.canCancel);
};

const SubscriptionLunaKeysTable = ({
  allowImmediateCancellation = false,
  cancelDescription = "This schedules Stripe billing to stop at the end of the current period. Access remains active until then.",
  cancelSuccessMessage = "Subscription cancelled immediately",
  cancelSubscriptionLunaKey,
  emptyMessage = "No subscription LunaKeys found",
  fetchSubscriptionLunaKeys,
  immediateCancelDescription = "This cancels Stripe billing immediately and deactivates the LunaKey tied to this subscription.",
  onCancelled,
  scheduleSuccessMessage = "Subscription cancellation scheduled",
  supportModalTitle = "Contact Support to Cancel",
  supportDescription = "Module reseller package subscriptions include multiple LunaKeys, so cancellation is handled manually by Lunova customer support. Please create a support ticket to request cancellation.",
  title = "Your Subscriptions",
}) => {
  const [subscriptionLunaKeys, setSubscriptionLunaKeys] = useState([]);
  const [subscriptionsLoading, setSubscriptionsLoading] = useState(false);
  const [subscriptionCurrentPage, setSubscriptionCurrentPage] = useState(1);
  const [subscriptionItemsPerPage, setSubscriptionItemsPerPage] = useState(25);
  const [subscriptionStatusFilter, setSubscriptionStatusFilter] =
    useState("active");
  const [subscriptionTotalPages, setSubscriptionTotalPages] = useState(0);
  const [totalSubscriptions, setTotalSubscriptions] = useState(0);
  const [cancelingLunaKeyId, setCancelingLunaKeyId] = useState(null);
  const [subscriptionToCancel, setSubscriptionToCancel] = useState(null);
  const [cancellationTiming, setCancellationTiming] = useState("period_end");

  const loadSubscriptionLunaKeys = useCallback(async () => {
    if (typeof fetchSubscriptionLunaKeys !== "function") return;

    setSubscriptionsLoading(true);
    try {
      const response = await fetchSubscriptionLunaKeys({
        page: subscriptionCurrentPage,
        limit: subscriptionItemsPerPage,
        status: subscriptionStatusFilter,
      });
      const pagination = response.pagination || {};

      setSubscriptionLunaKeys(response.items || []);
      setTotalSubscriptions(pagination.total || 0);
      setSubscriptionTotalPages(pagination.pages || 0);

      if (pagination.page && pagination.page !== subscriptionCurrentPage) {
        setSubscriptionCurrentPage(pagination.page);
      }
    } catch (error) {
      console.error("Error fetching subscription LunaKeys:", error);
      toast.error(error.message || "Failed to load subscription LunaKeys");
      setSubscriptionLunaKeys([]);
      setTotalSubscriptions(0);
      setSubscriptionTotalPages(0);
    } finally {
      setSubscriptionsLoading(false);
    }
  }, [
    fetchSubscriptionLunaKeys,
    subscriptionCurrentPage,
    subscriptionItemsPerPage,
    subscriptionStatusFilter,
  ]);

  useEffect(() => {
    loadSubscriptionLunaKeys();
  }, [loadSubscriptionLunaKeys]);

  const handleCancelSubscriptionLunaKey = (subscription) => {
    const lunaKey = subscription?.lunaKey;
    const canOpenCancelModal =
      canOpenSubscriptionCancellationModal(subscription);

    if (!canOpenCancelModal) return;
    if (
      !isManualSubscriptionCancellation(subscription) &&
      subscription?.canCancel &&
      !lunaKey?.id
    )
      return;

    setCancellationTiming("period_end");
    setSubscriptionToCancel(subscription);
  };

  const handleCloseCancelSubscriptionModal = () => {
    if (cancelingLunaKeyId) return;

    setSubscriptionToCancel(null);
  };

  const handleConfirmCancelSubscriptionLunaKey = async () => {
    const subscription = subscriptionToCancel;
    const lunaKey = subscription?.lunaKey;

    if (isExternalSubscriptionManagement(subscription)) return;
    if (isManualSubscriptionCancellation(subscription)) return;
    if (!subscription?.canCancel || !lunaKey?.id) return;
    if (typeof cancelSubscriptionLunaKey !== "function") return;

    const requestedCancellationTiming =
      allowImmediateCancellation && cancellationTiming === "immediate"
        ? "immediate"
        : "period_end";

    setCancelingLunaKeyId(lunaKey.id);
    try {
      await cancelSubscriptionLunaKey(lunaKey.id, {
        cancellationTiming: requestedCancellationTiming,
      });
      toast.success(
        requestedCancellationTiming === "immediate"
          ? cancelSuccessMessage
          : scheduleSuccessMessage
      );
      setSubscriptionToCancel(null);
      if (requestedCancellationTiming === "immediate") {
        setSubscriptionStatusFilter("cancelled");
        setSubscriptionCurrentPage(1);
      } else {
        await loadSubscriptionLunaKeys();
      }
      if (typeof onCancelled === "function") {
        await onCancelled();
      }
    } catch (error) {
      console.error("Error cancelling subscription LunaKey:", error);
      toast.error(error.message || "Failed to cancel subscription");
    } finally {
      setCancelingLunaKeyId(null);
    }
  };

  const handleSubscriptionItemsPerPageChange = (e) => {
    setSubscriptionItemsPerPage(Number(e.target.value));
    setSubscriptionCurrentPage(1);
  };

  const handleSubscriptionStatusFilterChange = (e) => {
    setSubscriptionStatusFilter(e.target.value);
    setSubscriptionCurrentPage(1);
  };

  const getSubscriptionUsageColor = (status) => {
    switch (status) {
      case "linked":
        return "badge-success";
      case "unused":
        return "badge-warning";
      case "package":
        return "badge-info";
      case "cancelled":
      case "expired":
        return "badge-secondary";
      case "missing":
        return "badge-danger";
      default:
        return "badge-info";
    }
  };

  const getBillingStatusColor = (status) => {
    switch (status) {
      case "active":
        return "badge-success";
      case "trialing":
      case "grace":
        return "badge-info";
      case "pending":
      case "past_due":
      case "unpaid":
      case "verification_unavailable":
        return "badge-warning";
      case "canceled":
      case "cancelled":
        return "badge-secondary";
      default:
        return "badge-secondary";
    }
  };

  const formatSubscriptionStatus = (status) => {
    if (status === "verification_unavailable") return "Needs Review";

    return (status || "unknown")
      .replace(/_/g, " ")
      .replace(/\b\w/g, (letter) => letter.toUpperCase());
  };

  const formatPlanLabel = (subscription) => {
    const plan = subscription?.plan || subscription?.interval || "subscription";

    if (plan === "monthly" || plan === "month") return "Monthly";
    if (plan === "quarterly" || plan === "quarter") return "Quarterly";
    if (plan === "yearly" || plan === "year") return "Yearly";

    return formatSubscriptionStatus(plan);
  };

  const getSubscriptionBillingSummary = (subscription) =>
    subscription?.billingSummary || {
      amount: subscription?.amount,
      currency: subscription?.currency,
      interval: subscription?.interval,
      intervalCount: subscription?.intervalCount,
      nextChargeAt: subscription?.currentPeriodEnd,
      latestInvoiceId: subscription?.latestInvoiceId,
      status: subscription?.billingStatus,
      cancelAtPeriodEnd: subscription?.cancelAtPeriodEnd,
    };

  const getProviderLabel = (subscription) =>
    subscription?.providerLabel ||
    (subscription?.provider === "amazon" ? "Amazon Appstore" : "Stripe");

  const getExternalManagementUrl = (subscription) =>
    subscription?.managementUrl || AMAZON_MANAGE_SUBSCRIPTION_URL;

  const formatSubscriptionAmount = (subscription) => {
    const billingSummary = getSubscriptionBillingSummary(subscription);

    try {
      return new Intl.NumberFormat("en-US", {
        style: "currency",
        currency: (billingSummary.currency || "usd").toUpperCase(),
      }).format(Number(billingSummary.amount) || 0);
    } catch {
      return `$${(Number(billingSummary.amount) || 0).toFixed(2)}`;
    }
  };

  const formatSubscriptionCadence = (subscription) => {
    const billingSummary = getSubscriptionBillingSummary(subscription);
    const interval = billingSummary.interval || subscription?.interval;
    const intervalCount = Number(billingSummary.intervalCount) || 1;

    if (interval === "year" && intervalCount === 1) return "year";
    if (interval === "month" && intervalCount === 3) return "quarter";
    if (interval === "month" && intervalCount === 1) return "month";
    if (interval === "week" && intervalCount === 1) return "week";
    if (interval === "day" && intervalCount === 1) return "day";
    if (interval) return `${intervalCount} ${interval}s`;

    return formatPlanLabel(subscription).toLowerCase();
  };

  const getPackageSummary = (subscription) =>
    subscription?.packageSummary || {
      totalLunaKeys: 0,
      activeLunaKeys: 0,
      availableLunaKeys: 0,
      linkedLunaKeys: 0,
      cancelledLunaKeys: 0,
      expiredLunaKeys: 0,
    };

  const formatLunaKeyCount = (count) =>
    `${Number(count) || 0} LunaKey${Number(count) === 1 ? "" : "s"}`;

  const formatPackageUsageSummary = (summary) =>
    `${summary.availableLunaKeys || 0} available / ${summary.linkedLunaKeys || 0} linked`;

  const formatSubscriptionDate = (date) => {
    if (!date) return "N/A";

    return new Date(date).toLocaleDateString();
  };

  const cancelLunaKey = subscriptionToCancel?.lunaKey;
  const cancelLinkedPlaylist = cancelLunaKey?.linkedPlaylist;
  const cancelUsageStatus = cancelLunaKey?.usageStatus || "missing";
  const isCancelModalBusy = cancelingLunaKeyId === cancelLunaKey?.id;
  const cancelBillingSummary =
    getSubscriptionBillingSummary(subscriptionToCancel);
  const cancelNextChargeAt =
    cancelBillingSummary.nextChargeAt || subscriptionToCancel?.currentPeriodEnd;
  const cancelPackageSummary = getPackageSummary(subscriptionToCancel);
  const isSupportCancellationModal =
    isManualSubscriptionCancellation(subscriptionToCancel);
  const isExternalManagementModal =
    isExternalSubscriptionManagement(subscriptionToCancel);
  const isImmediateCancellation =
    allowImmediateCancellation && cancellationTiming === "immediate";
  const activeCancelDescription = isImmediateCancellation
    ? immediateCancelDescription
    : cancelDescription;
  const activeCancelActionLabel = isImmediateCancellation
    ? "Cancel Immediately"
    : "Schedule Cancellation";
  const activeCancelBusyLabel = isImmediateCancellation
    ? "Cancelling"
    : "Scheduling";

  return (
    <>
      <div
        className="bg-white rounded-xl shadow-md border border-gray-100 dark:bg-gray-800/50 dark:border-gray-700/50 dark:backdrop-blur-sm"
        style={{ overflow: "visible" }}
      >
        <div className="p-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <h2 className="text-lg sm:text-xl font-semibold">
              {title} ({totalSubscriptions})
            </h2>
            <div className="flex flex-col sm:flex-row gap-2">
              <select
                className="input h-10 py-0 pr-9 text-sm leading-5"
                style={{ width: "150px" }}
                value={subscriptionStatusFilter}
                onChange={handleSubscriptionStatusFilterChange}
              >
                <option value="active">Active</option>
                <option value="cancelled">Cancelled</option>
                <option value="all">All Statuses</option>
              </select>
              <select
                className="input h-10 py-0 pr-9 text-sm leading-5"
                style={{ width: "140px" }}
                value={subscriptionItemsPerPage}
                onChange={handleSubscriptionItemsPerPageChange}
              >
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
                <option value={100}>100 per page</option>
              </select>
              <button
                className="btn btn-ghost w-full sm:w-auto justify-center"
                disabled={subscriptionsLoading}
                onClick={loadSubscriptionLunaKeys}
              >
                <i
                  className={`fas fa-rotate-right ${subscriptionsLoading ? "fa-spin" : ""}`}
                />
                <span>Refresh</span>
              </button>
            </div>
          </div>
        </div>

        <div
          className="overflow-x-auto"
          style={{ WebkitOverflowScrolling: "touch" }}
        >
          <table className="w-full" style={{ minWidth: "1220px" }}>
            <thead className="bg-gray-50 dark:bg-gray-700/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LunaKey
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Plan
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Amount
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Linked Account
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  LunaKey Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Billing Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Next Charge
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {subscriptionsLoading ? (
                Array.from({ length: 3 }).map((_, rowIndex) => (
                  <tr key={rowIndex}>
                    {Array.from({ length: 8 }).map((__, cellIndex) => (
                      <td className="px-6 py-4" key={cellIndex}>
                        <Shimmer className="h-4 w-full rounded-md" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : subscriptionLunaKeys.length === 0 ? (
                <tr>
                  <td
                    className="px-6 py-10 text-center text-gray-500 dark:text-gray-400"
                    colSpan="8"
                  >
                    {emptyMessage}
                  </td>
                </tr>
              ) : (
                subscriptionLunaKeys.map((subscription, index) => {
                  const { lunaKey } = subscription;
                  const packageSummary = getPackageSummary(subscription);
                  const isPackageSubscription = subscription.isResellerPackage;
                  const billingSummary =
                    getSubscriptionBillingSummary(subscription);
                  const linkedPlaylist = lunaKey?.linkedPlaylist;
                  const usageStatus = lunaKey?.usageStatus || "missing";
                  const isCanceling = cancelingLunaKeyId === lunaKey?.id;
                  const billingStatus =
                    billingSummary.status || subscription.billingStatus;
                  const cancelAtPeriodEnd =
                    billingSummary.cancelAtPeriodEnd ??
                    subscription.cancelAtPeriodEnd;
                  const renewsAutomatically = [
                    "active",
                    "trialing",
                    "past_due",
                    "unpaid",
                  ].includes(billingStatus);
                  const isCancelledSubscription =
                    ["canceled", "cancelled"].includes(billingStatus) ||
                    usageStatus === "cancelled";
                  const needsBillingReview =
                    billingStatus === "verification_unavailable";
                  const isExternalManaged =
                    isExternalSubscriptionManagement(subscription);
                  const nextChargeLabel = isCancelledSubscription
                    ? "Cancelled"
                    : cancelAtPeriodEnd
                      ? "Ends"
                      : needsBillingReview
                        ? "Needs Review"
                        : renewsAutomatically
                          ? "Charges"
                          : "No Charge";
                  const showNextChargeDate =
                    !isCancelledSubscription &&
                    (cancelAtPeriodEnd || renewsAutomatically);
                  const nextChargeAt =
                    billingSummary.nextChargeAt ||
                    subscription.currentPeriodEnd;
                  const nextChargeDate = nextChargeAt
                    ? formatSubscriptionDate(nextChargeAt)
                    : "Date unavailable";
                  const canOpenCancelModal =
                    canOpenSubscriptionCancellationModal(subscription);

                  return (
                    <tr
                      className={`${index % 2 === 0 ? "table-row-striped-even" : "table-row-striped-odd"} table-row-hover`}
                      key={`${subscription.subscriptionId}-${lunaKey?.id || subscription.latestTransactionId}`}
                    >
                      <td className="px-6 py-4">
                        <p className="font-mono text-sm font-semibold text-gray-900 dark:text-white">
                          {isPackageSubscription
                            ? "Module Package"
                            : lunaKey?.code || "Missing LunaKey"}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">
                          {isPackageSubscription
                            ? formatLunaKeyCount(packageSummary.totalLunaKeys)
                            : subscription.latestInvoiceId}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">
                        {formatPlanLabel(subscription)}
                      </td>
                      <td className="px-6 py-4">
                        <span className="font-semibold text-gray-900 dark:text-white">
                          {formatSubscriptionAmount(subscription)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        {isPackageSubscription ? (
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              Package Pool
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatPackageUsageSummary(packageSummary)}
                            </p>
                          </div>
                        ) : linkedPlaylist ? (
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {linkedPlaylist.name}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {linkedPlaylist.username}
                              {linkedPlaylist.domain
                                ? ` @ ${linkedPlaylist.domain}`
                                : ""}
                            </p>
                          </div>
                        ) : (
                          <div>
                            <p className="font-medium text-gray-900 dark:text-white">
                              {usageStatus === "unused"
                                ? "Unused LunaKey"
                                : "Not linked"}
                            </p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {formatSubscriptionStatus(usageStatus)}
                            </p>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`badge ${getSubscriptionUsageColor(
                            isPackageSubscription ? "package" : usageStatus
                          )}`}
                        >
                          {isPackageSubscription
                            ? "Package"
                            : formatSubscriptionStatus(usageStatus)}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span
                          className={`badge ${getBillingStatusColor(billingStatus)}`}
                          title={
                            billingStatus === "verification_unavailable"
                              ? "Billing status could not be verified."
                              : undefined
                          }
                        >
                          {formatSubscriptionStatus(billingStatus)}
                        </span>
                        <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                          {getProviderLabel(subscription)}
                        </p>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-600 dark:text-gray-300">
                        <span className="font-medium">{nextChargeLabel}</span>
                        {showNextChargeDate ? ` ${nextChargeDate}` : ""}
                      </td>
                      <td className="px-6 py-4 text-right">
                        {canOpenCancelModal ? (
                          <button
                            className={`btn btn-ghost btn-sm ml-auto disabled:opacity-50 disabled:cursor-not-allowed ${
                              isExternalManaged
                                ? "text-blue-600 hover:bg-blue-50 dark:text-blue-400 dark:hover:bg-blue-900/20"
                                : "text-red-600 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-900/20"
                            }`}
                            disabled={isCanceling}
                            onClick={() =>
                              handleCancelSubscriptionLunaKey(subscription)
                            }
                          >
                            <i
                              className={`fas ${
                                isCanceling
                                  ? "fa-spinner fa-spin"
                                  : isExternalManaged
                                    ? "fa-up-right-from-square"
                                    : "fa-ban"
                              }`}
                            />
                            <span>
                              {isCanceling
                                ? "Cancelling"
                                : isExternalManaged
                                  ? "Manage"
                                  : "Cancel"}
                            </span>
                          </button>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalSubscriptions > 0 && subscriptionLunaKeys.length > 0 && (
          <div className="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
              {`Showing ${Math.min((subscriptionCurrentPage - 1) * subscriptionItemsPerPage + 1, totalSubscriptions)} to ${Math.min(subscriptionCurrentPage * subscriptionItemsPerPage, totalSubscriptions)} of ${totalSubscriptions} subscriptions`}
            </p>
            <div className="flex gap-2 justify-center items-center">
              <button
                className="px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                disabled={subscriptionCurrentPage <= 1 || subscriptionsLoading}
                type="button"
                onClick={() =>
                  setSubscriptionCurrentPage((page) => Math.max(1, page - 1))
                }
              >
                Previous
              </button>
              <span className="text-xs sm:text-sm text-gray-600 dark:text-gray-300 px-1">
                Page {subscriptionCurrentPage} of {subscriptionTotalPages || 1}
              </span>
              <button
                className="px-3 py-1 text-xs sm:text-sm border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200"
                disabled={
                  subscriptionsLoading ||
                  subscriptionCurrentPage >= subscriptionTotalPages
                }
                type="button"
                onClick={() =>
                  setSubscriptionCurrentPage((page) =>
                    Math.min(subscriptionTotalPages || page, page + 1)
                  )
                }
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>

      <AnimatePresence>
        {subscriptionToCancel && (
          <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4">
            <motion.div
              animate={{ opacity: 1 }}
              className="absolute inset-0 bg-black/60 backdrop-blur-md"
              exit={{ opacity: 0 }}
              initial={{ opacity: 0 }}
              onClick={handleCloseCancelSubscriptionModal}
            />

            <motion.div
              animate={{ opacity: 1, scale: 1, y: 0 }}
              aria-describedby="cancel-subscription-description"
              aria-labelledby="cancel-subscription-title"
              aria-modal="true"
              className="relative w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200 dark:bg-gradient-to-b dark:from-gray-900 dark:to-gray-950 dark:ring-white/10"
              exit={{ opacity: 0, scale: 0.96, y: 12 }}
              initial={{ opacity: 0, scale: 0.96, y: 12 }}
              role="dialog"
              transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
            >
              <button
                aria-label="Close"
                className="absolute right-4 top-4 z-10 flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700 disabled:opacity-50 dark:hover:bg-white/10 dark:hover:text-white"
                disabled={isCancelModalBusy}
                type="button"
                onClick={handleCloseCancelSubscriptionModal}
              >
                <i className="fas fa-times text-sm" />
              </button>

              <div className="px-6 pt-6 pb-5">
                <div
                  className={`mb-4 flex h-12 w-12 items-center justify-center rounded-2xl ring-1 ${
                    isSupportCancellationModal || isExternalManagementModal
                      ? "bg-amber-50 text-amber-600 ring-amber-500/20 dark:bg-amber-500/10 dark:text-amber-300"
                      : "bg-red-50 text-red-500 ring-red-500/20 dark:bg-red-500/10 dark:text-red-300"
                  }`}
                >
                  <i
                    className={`${isExternalManagementModal ? "fab fa-amazon" : "fas fa-ban"} text-lg`}
                  />
                </div>

                <div>
                  <h2
                    className="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
                    id="cancel-subscription-title"
                  >
                    {isExternalManagementModal
                      ? `Manage in ${getProviderLabel(subscriptionToCancel)}`
                      : isSupportCancellationModal
                        ? supportModalTitle
                        : isImmediateCancellation
                          ? "Cancel LunaKey Subscription"
                          : "Schedule Subscription Cancellation"}
                  </h2>
                  <p
                    className="mt-2 text-sm leading-relaxed text-gray-600 dark:text-gray-300"
                    id="cancel-subscription-description"
                  >
                    {isExternalManagementModal
                      ? subscriptionToCancel.cancellationMessage ||
                        `${getProviderLabel(subscriptionToCancel)} manages this subscription. Open provider subscription settings to cancel or change billing.`
                      : isSupportCancellationModal
                        ? supportDescription
                        : activeCancelDescription}
                  </p>
                </div>

                <div
                  className={`mt-5 rounded-2xl border p-4 ${
                    isSupportCancellationModal || isExternalManagementModal
                      ? "border-amber-200 bg-amber-50/80 dark:border-amber-500/20 dark:bg-amber-500/10"
                      : "border-red-200 bg-red-50/80 dark:border-red-500/20 dark:bg-red-500/10"
                  }`}
                >
                  <p
                    className={`text-xs font-semibold uppercase tracking-wider ${
                      isSupportCancellationModal || isExternalManagementModal
                        ? "text-amber-700 dark:text-amber-300"
                        : "text-red-600 dark:text-red-300"
                    }`}
                  >
                    {isExternalManagementModal
                      ? "Amazon-managed subscription"
                      : isSupportCancellationModal
                        ? "Manual cancellation required"
                        : isImmediateCancellation
                          ? "You are cancelling immediately"
                          : "You are scheduling cancellation"}
                  </p>
                  <div className="mt-2 flex flex-wrap items-end gap-x-2 gap-y-1">
                    <span className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
                      {formatSubscriptionAmount(subscriptionToCancel)}
                    </span>
                    <span className="pb-1 text-sm font-medium text-gray-600 dark:text-gray-300">
                      / {formatSubscriptionCadence(subscriptionToCancel)}
                    </span>
                  </div>
                  <p className="mt-2 text-sm text-gray-600 dark:text-gray-300">
                    {isExternalManagementModal
                      ? `Cancellation and billing changes must be completed through ${getProviderLabel(subscriptionToCancel)}. Lunova access updates after Amazon confirms the subscription state.`
                      : isSupportCancellationModal
                        ? `${formatLunaKeyCount(
                            cancelPackageSummary.totalLunaKeys
                          )} are tied to this package subscription. Create a support ticket and a support team member will handle the cancellation manually.`
                        : isImmediateCancellation
                          ? `${formatPlanLabel(subscriptionToCancel)} subscription${
                              cancelNextChargeAt
                                ? `, next charge ${formatSubscriptionDate(
                                    cancelNextChargeAt
                                  )}`
                                : ""
                            }`
                          : `Access remains active until ${cancelNextChargeAt ? formatSubscriptionDate(cancelNextChargeAt) : "the current period ends"}.`}
                  </p>
                </div>

                {allowImmediateCancellation &&
                  !isSupportCancellationModal &&
                  !isExternalManagementModal && (
                    <div className="mt-5 rounded-xl border border-gray-200 bg-white p-1 dark:border-white/10 dark:bg-white/[0.04]">
                      <button
                        aria-pressed={cancellationTiming === "period_end"}
                        className={`flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          cancellationTiming === "period_end"
                            ? "bg-blue-50 text-blue-900 dark:bg-blue-500/10 dark:text-blue-100"
                            : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                        }`}
                        disabled={isCancelModalBusy}
                        type="button"
                        onClick={() => setCancellationTiming("period_end")}
                      >
                        <span
                          className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            cancellationTiming === "period_end"
                              ? "border-blue-600"
                              : "border-gray-400"
                          }`}
                        >
                          {cancellationTiming === "period_end" && (
                            <span className="h-2 w-2 rounded-full bg-blue-600" />
                          )}
                        </span>
                        <span>
                          <span className="block text-sm font-semibold">
                            Cancel at next renewal
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            Stops the next invoice and keeps access active
                            through the paid period.
                          </span>
                        </span>
                      </button>
                      <button
                        aria-pressed={cancellationTiming === "immediate"}
                        className={`mt-1 flex w-full items-start gap-3 rounded-lg px-4 py-3 text-left transition-colors disabled:cursor-not-allowed disabled:opacity-60 ${
                          cancellationTiming === "immediate"
                            ? "bg-red-50 text-red-900 dark:bg-red-500/10 dark:text-red-100"
                            : "text-gray-700 hover:bg-gray-50 dark:text-gray-300 dark:hover:bg-white/[0.04]"
                        }`}
                        disabled={isCancelModalBusy}
                        type="button"
                        onClick={() => setCancellationTiming("immediate")}
                      >
                        <span
                          className={`mt-1 flex h-4 w-4 shrink-0 items-center justify-center rounded-full border ${
                            cancellationTiming === "immediate"
                              ? "border-red-600"
                              : "border-gray-400"
                          }`}
                        >
                          {cancellationTiming === "immediate" && (
                            <span className="h-2 w-2 rounded-full bg-red-600" />
                          )}
                        </span>
                        <span>
                          <span className="block text-sm font-semibold">
                            Cancel immediately
                          </span>
                          <span className="mt-0.5 block text-xs text-gray-500 dark:text-gray-400">
                            Stops billing now and deactivates the LunaKey right
                            away.
                          </span>
                        </span>
                      </button>
                    </div>
                  )}

                <div className="mt-5 rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-white/10 dark:bg-white/[0.04]">
                  <div className="flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Plan
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {formatPlanLabel(subscriptionToCancel)}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Next Charge
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {cancelNextChargeAt
                        ? formatSubscriptionDate(cancelNextChargeAt)
                        : "Date unavailable"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Latest Invoice
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {cancelBillingSummary.latestInvoiceId ||
                        subscriptionToCancel.latestInvoiceId ||
                        "Unavailable"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {subscriptionToCancel.isResellerPackage
                        ? "Package Size"
                        : "LunaKey"}
                    </span>
                    <span className="truncate font-mono text-sm font-semibold text-gray-900 dark:text-white">
                      {subscriptionToCancel.isResellerPackage
                        ? formatLunaKeyCount(cancelPackageSummary.totalLunaKeys)
                        : cancelLunaKey?.code || "Missing LunaKey"}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      {subscriptionToCancel.isResellerPackage
                        ? "Pool Usage"
                        : "Linked Account"}
                    </span>
                    <span className="truncate text-sm font-medium text-gray-900 dark:text-white">
                      {subscriptionToCancel.isResellerPackage
                        ? formatPackageUsageSummary(cancelPackageSummary)
                        : cancelLinkedPlaylist?.name ||
                          (cancelUsageStatus === "unused"
                            ? "Unused LunaKey"
                            : "Not linked")}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Billing Status
                    </span>
                    <span
                      className={`badge ${getBillingStatusColor(cancelBillingSummary.status || subscriptionToCancel.billingStatus)}`}
                    >
                      {formatSubscriptionStatus(
                        cancelBillingSummary.status ||
                          subscriptionToCancel.billingStatus
                      )}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center justify-between gap-4">
                    <span className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-gray-400">
                      Provider
                    </span>
                    <span className="truncate text-sm font-semibold text-gray-900 dark:text-white">
                      {getProviderLabel(subscriptionToCancel)}
                    </span>
                  </div>
                </div>
              </div>

              {isExternalManagementModal ? (
                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-6 py-4 dark:border-white/10 dark:bg-gray-900/70 sm:flex-row sm:justify-end">
                  <button
                    className="rounded-full px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                    type="button"
                    onClick={handleCloseCancelSubscriptionModal}
                  >
                    Close
                  </button>
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-amber-500 px-5 py-2.5 text-sm font-semibold text-gray-950 shadow-lg shadow-amber-500/20 transition-all hover:bg-amber-400 active:scale-95"
                    type="button"
                    onClick={() =>
                      window.open(
                        getExternalManagementUrl(subscriptionToCancel),
                        "_blank",
                        "noopener,noreferrer"
                      )
                    }
                  >
                    <i className="fab fa-amazon" />
                    Open Amazon
                  </button>
                </div>
              ) : isSupportCancellationModal ? (
                <div className="flex border-t border-gray-100 bg-gray-50/80 px-6 py-4 dark:border-white/10 dark:bg-gray-900/70 sm:justify-end">
                  <button
                    className="rounded-full px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 dark:text-gray-300 dark:hover:bg-white/5"
                    type="button"
                    onClick={handleCloseCancelSubscriptionModal}
                  >
                    Close
                  </button>
                </div>
              ) : (
                <div className="flex flex-col-reverse gap-2 border-t border-gray-100 bg-gray-50/80 px-6 py-4 dark:border-white/10 dark:bg-gray-900/70 sm:flex-row sm:justify-end">
                  <button
                    className="rounded-full px-5 py-2.5 text-sm font-medium text-gray-600 transition-colors hover:bg-gray-100 disabled:opacity-50 dark:text-gray-300 dark:hover:bg-white/5"
                    disabled={isCancelModalBusy}
                    type="button"
                    onClick={handleCloseCancelSubscriptionModal}
                  >
                    Keep Subscription
                  </button>
                  <button
                    className="inline-flex items-center justify-center gap-2 rounded-full bg-red-500 px-5 py-2.5 text-sm font-semibold text-white shadow-lg shadow-red-500/20 transition-all hover:bg-red-600 active:scale-95 disabled:cursor-not-allowed disabled:opacity-60 disabled:shadow-none"
                    disabled={isCancelModalBusy}
                    type="button"
                    onClick={handleConfirmCancelSubscriptionLunaKey}
                  >
                    {isCancelModalBusy ? (
                      <>
                        <i className="fas fa-spinner fa-spin" />
                        {activeCancelBusyLabel}
                      </>
                    ) : (
                      <>
                        <i className="fas fa-ban" />
                        {activeCancelActionLabel}
                      </>
                    )}
                  </button>
                </div>
              )}
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </>
  );
};

export default SubscriptionLunaKeysTable;
